<script setup lang="ts">
import { computed, type Ref, ref } from 'vue';
import MagnifyingGlass from '@/assets/icons/MagnifyingGlass.vue';
import { Address } from '@/models/Address';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import { type AddressInputStatus } from '@/components/session/kyc/address/address.types';
import { watchIgnorable, useDebounceFn } from '@vueuse/core';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const addressInputStatus: Ref<AddressInputStatus> = ref('Input');
const model = defineModel({
  type: Address,
  required: true,
});

const props = defineProps({
  saveAddress: {
    type: Function,
    required: true,
  },
  loadedAddress: {
    type: String,
  },
});

const buttonText = computed(() => (addressInputStatus.value === 'Checking' ? 'Checking' : 'Next'));

const isNextEnabled = ref(false);
const isValidationVisible = ref(false);

const fullAddressString = ref(model.value?.fullAddress);

const onFullAddressStringUpdate = useDebounceFn(async (newFullAddressString: string) => {
  await checkData(newFullAddressString);
}, 500);

watchIgnorable(
  () => fullAddressString.value ?? '',
  async (newFullAddressString: string) => {
    await onFullAddressStringUpdate(newFullAddressString);
  }
);

async function checkData(newFullAddressString: string) {
  isValidationVisible.value = false;
  isNextEnabled.value = false;

  try {
    if (!newFullAddressString) {
      isValidationVisible.value = true;
      return;
    }

    addressInputStatus.value = 'Checking';

    model.value.fullAddress = newFullAddressString;

    isNextEnabled.value = true;
    isValidationVisible.value = false;
  } finally {
    addressInputStatus.value = 'Saving';
  }
}

async function onSaveAddress() {
  await props.saveAddress();

  return true;
}

async function init() {
  await checkData(fullAddressString.value);
}

function onBlur() {
  if (fullAddressString.value && model.value.fullAddress !== props.loadedAddress) {
    Mixpanel.instance.trackUnverifiableAddressEntered();
  }
}

init();
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col">
      <h1 class="text-3xl">What is your current residential address?</h1>
      <div class="mt-4 mb-4">Your address cannot be a PO box</div>
      <div>
        <div class="flex flex-1 flex-col" data-test="screen-address-unverifiable">
          <label for="address" class="text-light-primary mb-2 font-bold">Residential address</label>
          <div
            class="border-light-border text-light-primary dark:border-dark-brd-dark dark:bg-dark-list-bg dark:text-dark-primary relative h-12 rounded-md border font-normal"
          >
            <MagnifyingGlass class="absolute top-4 left-2" />
            <input
              name="address"
              id="address"
              type="text"
              data-test="address-input-auto"
              class="box-border flex h-full w-full bg-transparent pr-2 pl-7 text-base outline-none"
              placeholder="Start typing your address..."
              v-model="fullAddressString"
              autocomplete="off"
              @blur="onBlur"
            />
          </div>
        </div>
      </div>
      <p
        class="text-light-utility-red dark:text-dark-utility-red mt-1 py-0"
        v-if="isValidationVisible"
        data-test="address-validation-error"
      >
        <span>Address is required.</span>
      </p>
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
      <FlowNavigationButton
        :preNavigationAction="onSaveAddress"
        :text="buttonText"
        :disabled="!isNextEnabled"
        direction="forward"
      />
    </template>
  </KycSessionLayout>
</template>
